import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { Box } from '@mantine/core';
import type { ColumnOrderState, Table } from '@tanstack/react-table';
import { useEffect } from 'react';
import { flushSync } from 'react-dom';
import { ColumnItem } from './ColumnItem';
import { isColumnData } from './helpers';

export type ColumnListProps = {
  table: Table<any>;
  setColumnOrder: (newOrder: ColumnOrderState) => void;
};

export const ColumnList: React.FC<ColumnListProps> = ({
  table,
  setColumnOrder,
}) => {
  const columns = table
    .getAllLeafColumns()
    .filter(c => !c.columnDef.meta?.disableManagement);

  useEffect(
    () =>
      monitorForElements({
        canMonitor({ source }) {
          return isColumnData(source.data);
        },
        onDrop({ location, source }) {
          const target = location.current.dropTargets[0];
          if (!target) {
            return;
          }

          const sourceData = source.data;
          const targetData = target.data;

          if (!isColumnData(sourceData) || !isColumnData(targetData)) {
            return;
          }

          const indexOfSource = columns.findIndex(
            column => column.id === sourceData.columnId,
          );
          const indexOfTarget = columns.findIndex(
            column => column.id === targetData.columnId,
          );

          if (indexOfTarget < 0 || indexOfSource < 0) {
            return;
          }

          const closestEdgeOfTarget = extractClosestEdge(targetData);

          // Using `flushSync` so we can query the DOM straight after this line
          flushSync(() => {
            setColumnOrder(
              reorderWithEdge({
                list: columns,
                startIndex: indexOfSource,
                indexOfTarget,
                closestEdgeOfTarget,
                axis: 'vertical',
              }).map(c => c.id),
            );
          });
        },
      }),
    [columns],
  );

  return (
    <Box p={2}>
      {columns.map(column => (
        <ColumnItem key={column.id} column={column} />
      ))}
    </Box>
  );
};
